import { Button, Tile } from '@carbon/react';
import { WarningAlt } from '@carbon/react/icons';
import { Copy } from 'components/copy';
import { useOpenReportForm } from 'components/report-form';

/**
 * Enough to identify the fault. The full trace is already in Rollbar and Datadog.
 *
 * Exported so the test asserts against this rather than a second copy of the number.
 *
 * Deliberately NOT the same constant as the server's MAX_ERROR_STACK, which it happens to
 * equal. This one keeps a large payload off the wire; that one bounds what reaches a jsonb
 * column and a Jira description. They answer different questions and either could move
 * without the other — sharing a value across the language boundary would imply a coupling
 * that is not there. The server truncates regardless, so this is a courtesy, not the
 * guarantee.
 */
export const MAX_STACK_CHARS = 4000;

interface ErrorPageProps {
  /**
   * The error the boundary caught. Optional because the boundary can be given a plain
   * fallback with no error — a page that rendered this without one still works, it just
   * files a report with nothing but the URL.
   */
  error?: Error;
}

/**
 * What a person sees when a page throws.
 *
 * It used to end at "tell us in Slack", which asks someone who has just hit a wall to
 * change tools, find a channel, and describe from memory a thing they can no longer see.
 * Most people don't, and the ones who do lose the detail on the way. The button files the
 * report from here, with the error attached.
 *
 * The button opens the form; it does not file anything on its own. A report auto-submitted
 * from a crash would carry a stack trace and no account of what the person was trying to
 * do — which is the half that makes it reproducible — and would file a Jira ticket in their
 * name without their having asked. The copy says so rather than implying one click is the
 * end of it.
 *
 * @return {JSX.Element} The error screen.
 */
const ErrorPage = ({ error }: ErrorPageProps) => {
  // The form is mounted at the app shell, a sibling of the router rather than inside it,
  // so it survives the boundary that put this screen on the page. Without that, the one
  // moment someone most wants to report something would be the one moment they could not.
  const openReportForm = useOpenReportForm();

  const report = () =>
    openReportForm(
      'bug',
      error
        ? {
            message: error.message,
            stack: error.stack?.slice(0, MAX_STACK_CHARS),
          }
        : null
    );

  return (
    <Tile>
      <Copy
        heading={
          <>
            <WarningAlt size={32} /> There was an error.
          </>
        }
      >
        <p>Sorry!</p>

        <p>
          Please reload and try again. If this happens again, report it below.
          The error details and the page you were on are attached automatically
          — you just tell us what you were doing, and we will pick it up.
        </p>

        <p>
          <Button kind="primary" size="md" onClick={report}>
            Report this problem
          </Button>
        </p>
      </Copy>
    </Tile>
  );
};

export { ErrorPage };
